import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookMarked, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { termsApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { AI_LANG_NAMES } from "@/i18n";
import { GlossaryCard } from "@/components/glossary/GlossaryCard";
import { GlossaryDetail } from "@/components/glossary/GlossaryDetail";

const VIEWS = ["all", "recent", "revisited", "review", "understood", "flashcards"];

export default function Glossary() {
    const { t, lang } = useTranslation();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [view, setView] = useState("all");
    const [docFilter, setDocFilter] = useState("");
    const [domainFilter, setDomainFilter] = useState("");
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try { setItems(await termsApi.glossary(AI_LANG_NAMES[lang] || "")); }
        catch (e) { setItems([]); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    const documents = useMemo(() => {
        const m = new Map();
        items.forEach((i) => { if (i.document_id && !m.has(i.document_id)) m.set(i.document_id, i.document_title || i.document_id); });
        return [...m.entries()];
    }, [items]);
    const domains = useMemo(() => [...new Set(items.map((i) => i.domain || "general"))], [items]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        let list = items.filter((i) => !term || [i.term, i.definition, i.note, i.document_title].some((x) => (x || "").toLowerCase().includes(term)));
        if (docFilter) list = list.filter((i) => i.document_id === docFilter);
        if (domainFilter) list = list.filter((i) => (i.domain || "general") === domainFilter);
        if (view === "review") list = list.filter((i) => i.status === "review");
        else if (view === "understood") list = list.filter((i) => i.status === "understood");
        else if (view === "flashcards") list = list.filter((i) => i.in_flashcards);
        const sorted = [...list];
        if (view === "revisited") sorted.sort((a, b) => (b.revisits || 0) - (a.revisits || 0));
        else sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        return sorted;
    }, [items, q, view, docFilter, domainFilter]);

    const onChange = (updated, removedId) => {
        if (removedId) { setItems((prev) => prev.filter((i) => i.id !== removedId)); setSelected(null); return; }
        if (updated) {
            setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
            setSelected((s) => (s && s.id === updated.id ? { ...s, ...updated } : s));
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10" data-testid="glossary-page">
            <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><BookMarked className="h-5 w-5" /></div>
                <div>
                    <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("glossary.title")}</h1>
                    <p className="mt-1 text-t3">{t("glossary.subtitle")}</p>
                </div>
            </div>

            {items.length > 0 && (
                <div className="mt-6 flex flex-col gap-3">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-t4" />
                        <Input data-testid="glossary-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("glossary.search")} className="bg-surface-1 pl-9" />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex flex-wrap items-center gap-0.5 rounded-lg bg-surface-2 p-0.5">
                            {VIEWS.map((v) => (
                                <button key={v} data-testid={`glossary-filter-${v}`} onClick={() => setView(v)} className={cn("rounded-md px-2.5 py-1.5 text-xs transition-colors", view === v ? "bg-surface-3 text-t1 shadow-elev-1" : "text-t3 hover:text-t1")}>
                                    {t(`glossary.filters.${v}`)}
                                </button>
                            ))}
                        </div>
                        {documents.length > 1 && (
                            <Select value={docFilter || "__all"} onValueChange={(v) => setDocFilter(v === "__all" ? "" : v)}>
                                <SelectTrigger data-testid="glossary-doc-filter" className="h-9 w-[180px] bg-surface-1 text-xs"><SelectValue placeholder={t("glossary.allDocuments")} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all">{t("glossary.allDocuments")}</SelectItem>
                                    {documents.map(([id, title]) => <SelectItem key={id} value={id}>{title}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        )}
                        {domains.length > 1 && (
                            <Select value={domainFilter || "__all"} onValueChange={(v) => setDomainFilter(v === "__all" ? "" : v)}>
                                <SelectTrigger data-testid="glossary-domain-filter" className="h-9 w-[150px] bg-surface-1 text-xs"><SelectValue placeholder={t("glossary.allDomains")} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all">{t("glossary.allDomains")}</SelectItem>
                                    {domains.map((d) => <SelectItem key={d} value={d}>{t(`glossary.domain.${d}`)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
            ) : items.length === 0 ? (
                <div className="mt-8 rounded-3xl border border-dashed border-border bg-surface-1 px-6 py-16 text-center" data-testid="glossary-empty">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-[hsl(var(--primary))]"><BookMarked className="h-7 w-7" /></div>
                    <h3 className="mt-4 font-sans-display text-lg font-semibold text-t1">{t("glossary.empty.title")}</h3>
                    <p className="mx-auto mt-2 max-w-md text-sm text-t3">{t("glossary.empty.body")}</p>
                    <Button data-testid="glossary-empty-cta" className="mt-5 gap-2" onClick={() => navigate("/library")}>{t("glossary.empty.cta")} <ArrowRight className="h-4 w-4" /></Button>
                </div>
            ) : (
                <>
                    <div className="mono-label mt-4 text-t4">{t("glossary.count", { n: filtered.length })}</div>
                    {filtered.length === 0 ? (
                        <div className="mt-6 rounded-2xl border border-dashed border-border bg-surface-1 px-6 py-12 text-center text-sm text-t3" data-testid="glossary-no-results">{t("glossary.noResults")}</div>
                    ) : (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="glossary-grid">
                            {filtered.map((it) => <GlossaryCard key={it.id} item={it} onOpen={setSelected} />)}
                        </div>
                    )}
                </>
            )}

            <GlossaryDetail item={selected} onClose={() => setSelected(null)} onChange={onChange} />
        </div>
    );
}
